import { useCallback, useRef, useState } from "react";
import { Buffer } from "buffer";
import { useAppConfig } from "../AppConfigProvider";
import type { StreamAudioChunkPayload } from "./useLearningLlmStream";

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

function getTtsWsUrl(baseUrl: string, token: string): string {
  const url = new URL(baseUrl);
  url.searchParams.set("token", token);
  return url.toString();
}

export interface UseLearningTtsWebSocketOptions {
  onAudioChunk: (payload: StreamAudioChunkPayload) => void;
  getToken: () => Promise<string | null>;
  onStreamStarted?: () => void;
  onStreamEnded?: () => void;
}

export function useLearningTtsWebSocket({
  onAudioChunk,
  getToken,
  onStreamStarted,
  onStreamEnded,
}: UseLearningTtsWebSocketOptions) {
  const { currentDomain } = useAppConfig();
  const wsRef = useRef<WebSocket | null>(null);
  const seqRef = useRef(0);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSegmentsRef = useRef<Array<{ chunkIndex: number; text: string }>>([]);
  const [isConnected, setIsConnected] = useState(false);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptRef.current = MAX_RECONNECT_ATTEMPTS;
    pendingSegmentsRef.current = [];
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const tryConnect = async () => {
      const token = await getToken();
      if (!token) {
        return;
      }

      const baseUrl = `wss://${currentDomain}/backend/ws/tts`;
      const url = getTtsWsUrl(baseUrl, token);
      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        setIsConnected(true);
        const pending = pendingSegmentsRef.current;
        pendingSegmentsRef.current = [];
        pending.sort((a, b) => a.chunkIndex - b.chunkIndex);
        for (const { chunkIndex, text } of pending) {
          if (ws.readyState === WebSocket.OPEN && text.trim()) {
            try {
              ws.send(JSON.stringify({ type: "tts", text: text.trim(), chunkIndex }));
            } catch {
              // ignore send errors
            }
          }
        }
      };

      ws.onmessage = (event: MessageEvent) => {
        if (event.data instanceof ArrayBuffer) {
          const bytes = new Uint8Array(event.data);
          const audioBase64 = Buffer.from(bytes).toString("base64");
          seqRef.current += 1;
          onAudioChunk({
            seq: seqRef.current,
            audioBase64,
            isFinalChunk: false,
          });
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        setIsConnected(false);
        if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
          return;
        }
        const backoff = Math.min(INITIAL_BACKOFF_MS * Math.pow(2, reconnectAttemptRef.current), MAX_BACKOFF_MS);
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          tryConnect();
        }, backoff);
      };

      ws.onerror = () => {
        // onclose will run after error
      };
    };

    void tryConnect();
  }, [currentDomain, getToken, onAudioChunk]);

  const sendSegmentText = useCallback((chunkIndex: number, text: string) => {
    if (!text.trim()) {
      return;
    }
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "tts", text: text.trim(), chunkIndex }));
      } catch {
        // ignore send errors
      }
      return;
    }
    if (!ws || ws.readyState === WebSocket.CONNECTING) {
      pendingSegmentsRef.current.push({ chunkIndex, text });
    }
    // If CLOSING/CLOSED, drop (connection gone, no retry)
  }, []);

  const notifySegmentStart = useCallback(() => {
    onStreamStarted?.();
  }, [onStreamStarted]);

  const notifySegmentEnd = useCallback(() => {
    onStreamEnded?.();
  }, [onStreamEnded]);

  return {
    connect,
    disconnect,
    sendSegmentText,
    notifySegmentStart,
    notifySegmentEnd,
    isConnected,
  };
}
